import {
  Datagrid,
  EditButton,
  FunctionField,
  List,
  Loading,
  SelectField,
  ShowButton,
  TextField,
  useDataProvider,
  useNotify,
  useRefresh,
} from "react-admin";
import { isEmpty } from "ra-core";
import {
  cellContentCentered,
  dataGrid,  
  iconAction,
  listRoot,
  textColor,
} from "../Styles";
import { ActionTitles, DISPLAY_NAMES, RESOURCES, RESOURCES_BASEPATH } from "../Constants";
import ListActions from "../common/ListActions";
import { CustomPagination } from "../common/CustomPaginationComponent";
import cloneDeep from "lodash/cloneDeep";
import defaultsDeep from "lodash/defaultsDeep";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CustomTooltip from "../../utils/CustomTooltip";
import CustomDeleteWithConfirmButton from "../../utils/CustomDeleteWithConfirmButton";
import CustomDialog from "../../utils/CustomDialog";
import { ReportThemeFilter } from "./ReportThemeFilter";
import { Button } from "@mui/material";
import { useState } from "react";
import { useEffect } from "react";
import { Error404 } from "../errors/Error404";
import { infuseReportTypeIntoTemplates } from "../../mappers/ReportTheme";

//default

export const ReportThemeList = () => {

  const dataProvider = useDataProvider();
  const [clientChoices, setClientChoices] = useState();
  const [reportThemeChoices, setReportThemeChoices] = useState([]);
  const [reportTemplates, setReportTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();

  useEffect(() => {
    Promise.all([
      dataProvider.getClientNameList("admin/client/names"),
      dataProvider.getAllReportThemes(),
      dataProvider.getAllReportThemeTemplates(),
    ])
      .then(([clientData, reportTheme, templates]) => {
        setClientChoices(clientData.data);
        setReportThemeChoices(reportTheme.data);
        setReportTemplates(infuseReportTypeIntoTemplates(templates.data, reportTheme.data));
      })
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <Loading />;
  if (error) return <Error404 />;

  return (
    <List
      sx={listRoot}
      actions={
        <ListActions
          title={ActionTitles.REPORT_THEMES}
          basePath={RESOURCES_BASEPATH.REPORT_THEME}
          showCreateButton={true}
          recordName={ActionTitles.REPORT_THEMES}
          filters={
            <ReportThemeFilter 
              clientChoices={clientChoices} 
              reportThemeChoices={reportThemeChoices}
              reportTemplates={reportTemplates} 
            />
          }
        />
      }
      sort={{ field: "createdAt", order: "DESC" }}
      pagination={<CustomPagination />}
      empty={false}      
    >
      <ReportThemesGrid clientChoices={clientChoices} reportThemeChoices={reportThemeChoices} reportTemplates={reportTemplates} />
    </List>
  );
};

const ReportThemesGrid = (props) => {

  const { clientChoices, reportThemeChoices, reportTemplates } = props;

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [disableDelete, setDisableDelete] = useState(false);
  const [defaultDialogOpen, setDefaultDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState();

  const onDeleteSuccess = () => {
    notify(`${DISPLAY_NAMES[RESOURCES.REPORT_THEME]} Deleted`, { type: "info", autoHideDuration: 1000 });
    setDisableDelete(false);
    refresh();
  };

  const onDeleteFailure = (error) => {
    notify(error?.description ?? `Unable to delete the ${DISPLAY_NAMES[RESOURCES.REPORT_THEME]}`, { type: "warning", autoHideDuration: 2000 });
    setDisableDelete(false);
    refresh();
  };

  const handleSetDefaultClick = (record) => {
    setSelectedRecord(record);
    setDefaultDialogOpen(true);
  };

  const handleDefaultDialogClose = () => {
    setDefaultDialogOpen(false);
  };

  const confirmSetDefault = () => {
    dataProvider
      .setDefaultReportTheme({ id: selectedRecord.id })
      .then(() => {
        notify("Marked as default", { type: "info", autoHideDuration: 1000 });
        handleDefaultDialogClose();
        refresh();
      })
      .catch((error) => {
        notify(error?.message ?? "Unable to mark as default", { type: "warning", autoHideDuration: 2000 });
        handleDefaultDialogClose();
      });
  };

  return (
    <>
    <Datagrid
      sx={defaultsDeep(
        cloneDeep(dataGrid.base),
        cloneDeep(dataGrid.firstColSticky),
      )}
      bulkActionButtons={false}
    >
      <SelectField source="clientId" label="Client" choices={clientChoices} sortable={false} />
      <SelectField source="reportThemeId" label="Report Theme" choices={reportThemeChoices} sortable={false} />
      <SelectField source="reportThemeTemplateId" label="Report Template" choices={reportTemplates} emptyText="--" sortable={false} />
      <FunctionField
        sx={cellContentCentered}
        source="default"
        label="Default"
        sortable={false}
        render={(record) => (
          record.default ? (
            <CheckCircleOutlinedIcon sx={textColor.green} />
          ) : (
            <CustomTooltip title="Click to set as default" placement="top">
              <Button
                id={`report-theme-list-set-default-button-${record.id}`}
                onClick={() => handleSetDefaultClick(record)}
                sx={iconAction}
              >
                <CancelOutlinedIcon sx={textColor.red} />
              </Button>
            </CustomTooltip>
          )
        )}
      />
      <TextField source="createdAt" label="Last Modified" />
      <FunctionField
        sx={cellContentCentered}
        source="View"
        label={false}
        sortable={false}
        render={(record) => (
          <CustomTooltip title="View" placement="top">
            <ShowButton
              id={`report-theme-list-show-button-${record.id}`}
              icon={<RemoveRedEyeOutlinedIcon />}
              sx={iconAction}
              size="medium"
              label={""}
              basePath={RESOURCES_BASEPATH.REPORT_THEME}
              record={record}
            ></ShowButton>
          </CustomTooltip>
        )}
      />
      <FunctionField
        sx={cellContentCentered}
        source="Edit"
        label={false}
        sortable={false}
        render={(record) => (
          <CustomTooltip title="Edit" placement="top">
            <EditButton
              id={`report-theme-list-edit-button-${record.id}`}
              icon={<EditOutlinedIcon />}
              sx={iconAction}
              size="medium"
              label={""}
              basePath={RESOURCES_BASEPATH.REPORT_THEME}
              record={record}
              disabled={!isEmpty(record.reportThemeTemplateId)}
            ></EditButton>
          </CustomTooltip>
        )}
      />
      <FunctionField
        sx={cellContentCentered}
        source="Delete"
        label={false}
        sortable={false}
        render={(record) => (
          <CustomTooltip title="Delete" placement="top">
            <CustomDeleteWithConfirmButton
              id={`report-theme-list-delete-button-${record.id}`}
              icon={<DeleteOutlinedIcon />}
              sx={iconAction}
              size="medium"
              label={""}
              record={record}
              recordName={DISPLAY_NAMES[RESOURCES.REPORT_THEME]}
              mutationMode="pessimistic"
              resource={RESOURCES.REPORT_THEME}
              onSuccess={onDeleteSuccess}
              onFailure={onDeleteFailure}
              disableDelete={disableDelete}
              setDisableDelete={setDisableDelete}
            />
          </CustomTooltip>
        )}
      />
    </Datagrid>
    <CustomDialog
      openDialog={defaultDialogOpen}
      closeDialog={handleDefaultDialogClose}
      title="Set as Default"
      content={<p>Are you sure you want to mark this {DISPLAY_NAMES[RESOURCES.REPORT_THEME]} as default?</p>}
      handleSubmit={confirmSetDefault}
      submitBtnTitle="Confirm"
      hideDivider={true}
    />
    </>
  );
};